import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';

const MilestoneAnimation = ({ milestone, onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (milestone) {
      setIsVisible(true);
      setIsAnimating(true);
      
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => {
          setIsVisible(false);
          onComplete();
        }, 500);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [milestone, onComplete]);

  if (!isVisible || !milestone) {
    return null;
  }

  const getMilestoneConfig = (percentage) => {
    switch (percentage) {
      case 25:
        return {
          icon: 'Zap',
          title: 'Quarter Complete!',
          message: 'Great progress so far',
          color: 'text-warning',
          bgColor: 'bg-warning/10'
        };
      case 50:
        return {
          icon: 'Target',
          title: 'Halfway There!',
          message: 'You\'re doing amazing',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        };
      case 75:
        return {
          icon: 'TrendingUp',
          title: 'Almost Done!',
          message: 'Just a little bit more',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        };
      case 100:
        return {
          icon: 'CheckCircle',
          title: 'Sync Complete!',
          message: 'All tracks processed successfully',
          color: 'text-success',
          bgColor: 'bg-success/10'
        };
      default:
        return {
          icon: 'Star',
          title: 'Milestone Reached!',
          message: `${percentage}% complete`,
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        };
    }
  };

  const config = getMilestoneConfig(milestone.percentage);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1050 p-4">
      <div className={`bg-surface border border-border rounded-lg p-8 max-w-sm w-full text-center transform transition-all duration-500 ${
        isAnimating 
          ? 'scale-100 opacity-100' :'scale-95 opacity-0'
      }`}>
        {/* Animated Icon */}
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${config.bgColor} mb-4 ${
          isAnimating ? 'animate-bounce' : ''
        }`}>
          <Icon 
            name={config.icon} 
            size={32} 
            className={config.color}
          />
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-text-primary mb-2">
          {config.title}
        </h3>

        {/* Message */}
        <p className="text-text-secondary mb-4">
          {config.message}
        </p>

        {/* Progress Indicator */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-text-muted">Progress</span>
            <span className={`font-medium ${config.color}`}>
              {milestone.percentage}%
            </span>
          </div>
          <div className="w-full bg-surface-secondary rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ease-out ${
                milestone.percentage === 25 ? 'bg-warning' :
                milestone.percentage === 50 ? 'bg-primary' :
                milestone.percentage === 75 ? 'bg-accent': 'bg-success'
              }`}
              style={{ width: `${milestone.percentage}%` }}
            ></div>
          </div>
        </div>

        {/* Stats */}
        {milestone.stats && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-muted">Processed:</span>
                <span className="ml-1 font-medium text-text-primary">
                  {milestone.stats.processed}
                </span>
              </div>
              <div>
                <span className="text-text-muted">Success:</span>
                <span className="ml-1 font-medium text-success">
                  {milestone.stats.successful}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Confetti Effect for 100% */}
        {milestone.percentage === 100 && isAnimating && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-primary rounded-full animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              ></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MilestoneAnimation;